import { MenuOutlined } from '@ant-design/icons';
import { Layout, Button, Dropdown } from 'antd';
import Link from 'antd/es/typography/Link';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useLayoutLogin } from '@app/components/LayoutLogin/hooks';
import AppStore from '@app/components/LayoutLogin/icons/AppStore.svg';
import ByhelpLogo from '@app/components/LayoutLogin/icons/ByhelpLogo.svg';
import CirclesGroup from '@app/components/LayoutLogin/icons/CirclesGroup.svg';
import GooglePlay from '@app/components/LayoutLogin/icons/GooglePlay.svg';
import {
  Breadcrumbs,
  ContactInformation,
  LegalInformation,
  LogoSpace,
  NavigationColumn,
  RegisterHint,
} from '@app/components/LayoutLogin/ui/components';
import { AUTHORIZATION_PATH, REGISTRATION_PATH } from '@app/constants';

import type { MenuProps } from 'antd';
import type { FC } from 'react';

import '@app/components/LayoutLogin/ui/LayoutLogin.scss';

const { Header, Footer, Content } = Layout;

interface LayoutLoginProps {
  children: React.ReactNode;
}

export const LayoutLogin: FC<LayoutLoginProps> = ({ children }) => {
  const { isRegistrationMode, isAuthorizationMode, currentYear } =
    useLayoutLogin();

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: (
          <RouterLink to="https://byhelp.ru/#functions">Функционал</RouterLink>
        ),
      },
      {
        key: '2',
        label: <RouterLink to="https://byhelp.ru/price">Цены</RouterLink>,
      },
      {
        key: '3',
        label: (
          <RouterLink to="https://byhelp.ru/contacts">Контакты</RouterLink>
        ),
      },
    ],
    []
  );

  return (
    <Layout className="LayoutLogin">
      <Header className="LayoutLogin__header">
        <div className="LayoutLogin__header-container">
          <div className="LayoutLogin__header-left">
            <Link
              href="https://byhelp.ru"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="LayoutLogin__logo">
                <img src={ByhelpLogo} alt="ByHelp" />
              </div>
            </Link>

            <nav className="LayoutLogin__header-nav">
              <RouterLink
                to="https://byhelp.ru/#functions"
                className="LayoutLogin__header-nav-link"
              >
                Функционал
              </RouterLink>
              <RouterLink
                to="https://byhelp.ru/price"
                className="LayoutLogin__header-nav-link"
              >
                Цены
              </RouterLink>
              <RouterLink
                to="https://byhelp.ru/contacts"
                className="LayoutLogin__header-nav-link"
              >
                Контакты
              </RouterLink>
            </nav>
          </div>

          <div className="LayoutLogin__header-actions">
            <div className="LayoutLogin__social-links">
              <LogoSpace />
            </div>

            <div className="LayoutLogin__auth-buttons">
              <RouterLink to={AUTHORIZATION_PATH}>
                <Button
                  className={`LayoutLogin__auth-btn${
                    isAuthorizationMode ? ' LayoutLogin__auth-btn--active' : ''
                  }`}
                >
                  Войти
                </Button>
              </RouterLink>
              <RouterLink to={REGISTRATION_PATH}>
                <Button
                  className={`LayoutLogin__auth-btn${
                    isRegistrationMode ? ' LayoutLogin__auth-btn--active' : ''
                  }`}
                >
                  Регистрация
                </Button>
              </RouterLink>

              <div className="LayoutLogin__burger-menu">
                <Dropdown menu={{ items: menuItems }}>
                  <MenuOutlined />
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </Header>

      <Content className="LayoutLogin__content">
        <div className="LayoutLogin__breadcrumb">
          <Breadcrumbs
            isRegistrationMode={isRegistrationMode}
            isAuthorizationMode={isAuthorizationMode}
          />
        </div>
        <div className="LayoutLogin__content-wrapper">
          {children}
          {isRegistrationMode && <RegisterHint />}
        </div>
      </Content>

      <Footer className="LayoutLogin__footer">
        {/* Десктопный layout */}
        <div className="LayoutLogin__footer-columns-desktop">
          <div className="LayoutLogin__footer-columns-inner">
            <NavigationColumn />
            {/* TODO временно скрываем */}
            {/* <CapabilityColumn /> */}
            <ContactInformation />
          </div>
          <LegalInformation />
        </div>

        {/* Мобильный layout */}
        <div className="LayoutLogin__footer-columns-mobile">
          <div className="LayoutLogin__footer-col">
            <NavigationColumn />
            <LegalInformation />
          </div>
          <div className="LayoutLogin__footer-col">
            <ContactInformation />
            <img
              src={ByhelpLogo}
              alt=""
              className="LayoutLogin__footer-brand-logo"
              role="presentation"
            />
          </div>
        </div>

        <div className="LayoutLogin__footer-bottom">
          <div className="LayoutLogin__footer-bottom-content">
            <div className="LayoutLogin__footer-brand">
              <div className="LayoutLogin__footer-brand-text">
                <div>ООО "БИЗНЕС РЕШЕНИЕ"</div>
                <div>КПП: 504701001</div>
                <div>ОГРН: 1215000031278</div>
                <div>ИНН: 5047249666</div>
              </div>
              <div className="LayoutLogin__footer-copyright">
                © {currentYear} ByHelp. Все права защищены
              </div>
            </div>

            <div className="LayoutLogin__footer-version">
              <div className="LayoutLogin__app-links">
                <Link
                  href="https://apps.apple.com/ru/app/byhelp-app/id6739260257"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="LayoutLogin__app-link"
                >
                  <img src={AppStore} alt="App Store" />
                </Link>
                <Link
                  href="https://play.google.com/store/apps/details?id=com.byhelp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="LayoutLogin__app-link"
                >
                  <img src={GooglePlay} alt="Google Play" />
                </Link>
                {/* TODO временно скрываем */}
                {/* <RouterLink to="/"><img src={Rustore} alt="RuStore" /></RouterLink> */}
              </div>
              <div className="LayoutLogin__footer-dev-text">
                Разработка сайта
              </div>
            </div>
          </div>

          <div className="LayoutLogin__circles-wrapper">
            <img
              src={CirclesGroup}
              alt=""
              className="LayoutLogin__circles-group"
              role="presentation"
            />
          </div>
          <img
            src={ByhelpLogo}
            alt=""
            className="LayoutLogin__footer-decor-logo"
            role="presentation"
          />
        </div>
      </Footer>
    </Layout>
  );
};
